import { MAX_OPTIONS_PER_COMPONENT } from '../supplierExchange/stagedUpload';

/**
 * The New Client Link wizard, as rules.
 *
 * A CG member picks a company and a contact, attaches one design proof per
 * component, sets a price, and gets back a /review/[token] URL to send.
 *
 * ── One proof per component, not five ──────────────────────────────────────
 *
 * The supplier's dialog stages up to five options per component because TAP
 * picks a winner from what a factory sends. This is the opposite direction: a
 * client is shown what TAP has decided to propose, and being asked to choose
 * between five charms is how a sign-off turns into a design meeting. Exactly
 * one proof per component, and the sign-off gate downstream
 * (issue_signoff_certificate) counts on that being true.
 *
 * ── Why the steps are ordered ──────────────────────────────────────────────
 *
 * The order row must exist before any asset can be written to it —
 * order_assets.cg_order_id is NOT NULL and guard_option_insert's sibling
 * checks the parent. Same constraint the supplier dialog works around, and the
 * same answer: hold the files in the browser, create the order on submit, then
 * upload against its id.
 *
 * The token is minted LAST, and that ordering is load-bearing. A token created
 * before the assets landed is a live public URL pointing at an empty review —
 * and the whole point of it is that a client opens it and sees four things.
 */

/** Proofs per component. One, deliberately — see the note above. */
export const PROOFS_PER_COMPONENT = 1;

export interface ClientLinkDraft {
  companyId: string;
  leadId: string;
  /** Component name to the chosen file. Absent means not chosen yet. */
  proofs: Record<string, File | undefined>;
  /**
   * The order number. Suggested by nextOrderNo() and editable, because
   * next_cg_order_no() is revoked from authenticated — only the
   * order-from-sample function may call it — so the browser has to propose one
   * and let the unique constraint be the referee.
   */
  orderNo: string;
  /** Must be one of cg_product_types: cg_order_items has a foreign key to it. */
  productType: string;
  description: string;
  unitPriceUsd: string;
  units: string;
  /** How long the review link stays live. */
  expiryDays: string;
}

export function emptyDraft(): ClientLinkDraft {
  return {
    companyId: '',
    leadId: '',
    proofs: {},
    orderNo: '',
    productType: '',
    description: '',
    unitPriceUsd: '',
    units: '',
    expiryDays: '30',
  };
}

export interface DraftProblem {
  field: 'company' | 'lead' | 'proofs' | 'orderNo' | 'productType' | 'price' | 'units' | 'expiry';
  message: string;
}

/**
 * The next order number, proposed from the ones that exist.
 *
 * The database has next_cg_order_no(), but it is revoked from `authenticated`
 * on purpose: it is for the order-from-sample path, which runs SECURITY
 * DEFINER. So the wizard proposes and the unique index decides — two people
 * opening the wizard at once get the same suggestion and the second submit
 * fails loudly rather than quietly renumbering somebody's order.
 *
 * Only `#<digits>` counts. An order numbered by hand as "RUSH-4" is not a
 * sequence position and must not drag the next number to 5.
 */
export function nextOrderNo(existing: { order_no: string }[]): string {
  let highest = 1000;
  for (const order of existing) {
    const match = /^#(\d+)$/.exec(order.order_no.trim());
    if (match !== null) {
      highest = Math.max(highest, Number(match[1]));
    }
  }
  return `#${String(highest + 1)}`;
}

/**
 * Everything wrong with the draft, not just the first thing.
 *
 * Reported as a list so the wizard can mark every bad field at once. A form
 * that surfaces one problem per submit makes somebody press the button four
 * times to learn four things.
 */
export function draftProblems(
  draft: ClientLinkDraft,
  components: readonly string[],
): DraftProblem[] {
  const problems: DraftProblem[] = [];

  if (draft.companyId.trim() === '') {
    problems.push({ field: 'company', message: 'Choose the client company.' });
  }
  if (draft.leadId.trim() === '') {
    problems.push({ field: 'lead', message: 'Choose the contact who will review.' });
  }

  const missing = components.filter((component) => draft.proofs[component] === undefined);
  if (missing.length > 0) {
    problems.push({
      field: 'proofs',
      // Named, not counted. "One proof is missing" sends somebody hunting.
      message: `Attach a proof for ${missing.join(', ')}.`,
    });
  }

  if (!/^#?\S+$/.test(draft.orderNo.trim())) {
    problems.push({ field: 'orderNo', message: 'Give the order a number.' });
  }

  if (draft.productType.trim() === '') {
    // cg_order_items.product_type is a foreign key. A blank one fails at the
    // insert, after the order row is already written and cannot be tidied up
    // from here — cg_orders has no DELETE grant for this path.
    problems.push({ field: 'productType', message: 'Choose what is being made.' });
  }

  const price = Number(draft.unitPriceUsd);
  if (draft.unitPriceUsd.trim() === '' || !Number.isFinite(price) || price <= 0) {
    problems.push({ field: 'price', message: 'Enter a unit price above zero.' });
  }

  const units = Number(draft.units);
  if (
    draft.units.trim() === '' ||
    !Number.isFinite(units) ||
    !Number.isInteger(units) ||
    units <= 0
  ) {
    problems.push({ field: 'units', message: 'Enter a whole number of units.' });
  }

  const days = Number(draft.expiryDays);
  if (!Number.isFinite(days) || !Number.isInteger(days) || days < 1) {
    problems.push({ field: 'expiry', message: 'The link must stay live for at least a day.' });
  }

  return problems;
}

export function isReady(draft: ClientLinkDraft, components: readonly string[]): boolean {
  return draftProblems(draft, components).length === 0;
}

/** Proofs in component order, so uploads are numbered the way they are shown. */
export function orderedProofs(
  draft: ClientLinkDraft,
  components: readonly string[],
): { component: string; file: File }[] {
  const ready: { component: string; file: File }[] = [];
  for (const component of components) {
    const file = draft.proofs[component];
    if (file !== undefined) {
      ready.push({ component, file });
    }
  }
  return ready;
}

/**
 * Attach a proof, replacing whatever was there.
 *
 * Replacement rather than accumulation: this wizard sends ONE proof per
 * component, and the version history begins after the client has seen it —
 * a second file chosen before anything has been sent is a correction, not a
 * v2. Versions start at v1 and only advance through a client's revision
 * request (upload_order_asset).
 */
export function attachProof(
  draft: ClientLinkDraft,
  component: string,
  file: File,
): ClientLinkDraft {
  return { ...draft, proofs: { ...draft.proofs, [component]: file } };
}

export function removeProof(draft: ClientLinkDraft, component: string): ClientLinkDraft {
  const proofs = { ...draft.proofs };
  delete proofs[component];
  return { ...draft, proofs };
}

/** The link a client is sent. Built from the app origin, never hard-coded. */
export function reviewUrl(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}/review/${encodeURIComponent(token)}`;
}

/**
 * A sanity bound shared with the supplier side.
 *
 * Imported rather than restated so that if the per-component cap ever moves,
 * the one place that must NOT quietly follow it is visible: this wizard sends
 * one proof regardless, and the assertion below says why.
 */
export function proofsFitTheRound(): boolean {
  return PROOFS_PER_COMPONENT <= MAX_OPTIONS_PER_COMPONENT;
}
